import { useState, useMemo } from "react";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { rupiah, tanggal, angka } from "@/lib/format";
import { HandCoins, Users, FileWarning, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openPdf } from "@/pages/Proforma";
import { toast } from "sonner";

export default function Outstanding() {
  const { data: clients } = useFetch("/clients");
  const { data: projects } = useFetch("/projects");
  const [clientId, setClientId] = useState("all");
  const [projectId, setProjectId] = useState("all");

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (clientId !== "all") p.set("client_id", clientId);
    if (projectId !== "all") p.set("project_id", projectId);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [clientId, projectId]);

  const { data, loading } = useFetch(`/outstanding${qs}`, [qs]);
  const projOptions = (projects || []).filter((p) => clientId === "all" || p.client_id === clientId);

  const exportPdf = () => {
    if (clientId === "all") { toast.error("Pilih satu klien dulu untuk export statement"); return; }
    const q = projectId !== "all" ? `?project_id=${projectId}` : "";
    openPdf(`/statements/${clientId}/pdf${q}`);
  };

  const agingTone = (b, age) => b === "Belum Jatuh Tempo"
    ? "bg-blue-50 text-blue-700 border-blue-200"
    : age > 60 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div>
      <PageHeader title="Outstanding" subtitle="Rincian invoice belum lunas per klien — filter proyek atau ringkasan semua klien"
        action={<Button onClick={exportPdf} disabled={clientId === "all"} data-testid="outstanding-export-pdf" className="bg-[#9333EA] hover:bg-[#7e22ce]"><FileDown className="h-4 w-4 mr-1" /> Export PDF (Statement Klien)</Button>} />

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Klien</Label>
            <Select value={clientId} onValueChange={(v) => { setClientId(v); setProjectId("all"); }}>
              <SelectTrigger data-testid="outstanding-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Klien (Ringkasan)</SelectItem>
                {(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Proyek</Label>
            <Select value={projectId} onValueChange={setProjectId} disabled={clientId === "all"}>
              <SelectTrigger data-testid="outstanding-project"><SelectValue placeholder="Semua Proyek" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Proyek</SelectItem>
                {projOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading || !data ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Kpi label="Total Outstanding" value={rupiah(data.grand_total.outstanding)} icon={HandCoins} tone="rose" />
            <Kpi label="Jumlah Invoice" value={angka(data.grand_total.invoice_count)} icon={FileWarning} tone="amber" />
            <Kpi label="Klien Berpiutang" value={angka(data.summary.length)} icon={Users} tone="violet" />
          </div>

          <h3 className="font-display text-lg font-semibold mb-3">Ringkasan per Klien</h3>
          <div className="mb-8">
            <DataTable testId="outstanding-summary" data={data.summary} pageSize={8} searchable={data.summary.length > 5}
              columns={[
                { key: "client", label: "Klien" },
                { key: "invoice_count", label: "Jml Invoice", numeric: true },
                { key: "total", label: "Total Invoice", numeric: true, render: (r) => rupiah(r.total) },
                { key: "paid", label: "Dibayar", numeric: true, render: (r) => rupiah(r.paid) },
                { key: "outstanding", label: "Outstanding", numeric: true, render: (r) => <span className="font-semibold text-rose-500">{rupiah(r.outstanding)}</span> },
              ]}
              onRowClick={(r) => { setClientId(r.client_id); setProjectId("all"); }} />
          </div>

          <h3 className="font-display text-lg font-semibold mb-3">Rincian Invoice{clientId !== "all" ? " (Terfilter)" : ""}</h3>
          <DataTable testId="outstanding-details" data={data.details} pageSize={12}
            columns={[
              { key: "number", label: "No. Invoice" },
              { key: "client", label: "Klien" },
              { key: "project", label: "Proyek" },
              { key: "billing_start", label: "Periode", render: (r) => `${tanggal(r.billing_start)} – ${tanggal(r.billing_end)}` },
              { key: "grand_total", label: "Total", numeric: true, render: (r) => rupiah(r.grand_total) },
              { key: "paid", label: "Dibayar", numeric: true, render: (r) => rupiah(r.paid) },
              { key: "outstanding", label: "Outstanding", numeric: true, render: (r) => <span className="font-semibold text-rose-500">{rupiah(r.outstanding)}</span> },
              { key: "due_date", label: "Jatuh Tempo", render: (r) => tanggal(r.due_date) },
              { key: "aging_bucket", label: "Aging", render: (r) => <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${agingTone(r.aging_bucket, r.age_days)}`}>{r.aging_bucket}</span> },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            ]} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const t = { rose: "bg-rose-500/15 text-rose-300", amber: "bg-amber-500/15 text-amber-300", violet: "bg-violet-500/15 text-violet-300" }[tone];
  return (
    <Card className="p-6 flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
        <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
      </div>
      <div className={`h-10 w-10 rounded-md flex items-center justify-center ${t}`}><Icon className="h-5 w-5" /></div>
    </Card>
  );
}
